import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Checkbox, FormControlLabel, Button, Typography,
  TextField, Stack, Divider, Tooltip, IconButton, Avatar, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import axios from 'axios';
import config from '../config/config';
import SnackbarAlert from '../components/SnackbarAlert';
import SimpleSuccessSpinner from '../components/LoadingSpinner';
import DoubleConfirmModal from '../components/DoubleConfirmModal';
import { Items } from '../../src/modules/Menu/Items';

const ACCENT = '#0655A5';

const elegantInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#fbfbfd',
    transition: 'box-shadow .15s, border-color .15s',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
    '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(6,85,165,0.12)' },
  },
  '& .MuiInputLabel-root': { fontWeight: 600 },
  '& .MuiFormHelperText-root': { ml: 0 },
};

const Section = ({ title, subtitle, children, highlight = false }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 2,
      mb: 1.5,
      ...(highlight ? { borderLeft: `4px solid ${ACCENT}`, pl: 1.25 } : {}),
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

// 🔹 Nombres de permisos para la bitácora
const getNombresPermisos = (ids) =>
  ids.map(id => Items.find(item => item.id === id)?.title || `ID desconocido: ${id}`).join(', ');

// 🔹 Calcular ruta inicial a partir de los permisos
const getRutaInicialFromPermissions = (perms) => {
  if (!Array.isArray(perms) || perms.length === 0) return '/Solicitudes';

  const minId = Math.min(...perms);
  const item = Items.find(it => it.id === minId);

  const ruta = item?.path || '/Solicitudes';
  return ruta;
};

const ModalPerfiles = ({ open, onClose, onSave, userId, handleInputChange, formData }) => {
  const [permissions, setPermissions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [query, setQuery] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const loggedInUsername = sessionStorage.getItem('loggedInUsername');

  // Cargar permisos al abrir (solo si es edición)
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setPermissions([]);
    setSelectAll(false);
    setQuery('');
    if (!userId) return;

    (async () => {
      try {
        setFetching(true);
        const res = await axios.get(`${config.apiBaseUrl}/Permisos/${userId}`);
        const permisosStr = res.data?.permisos || '';
        const arr = permisosStr ? permisosStr.split(',').map(Number).filter(Boolean) : [];
        setPermissions(arr);
      } catch (error) {
        console.error('Error al cargar los permisos:', error);
        setSnackbarMessage('Hubo un error al cargar los permisos.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setFetching(false);
      }
    })();
  }, [open, userId]);

  useEffect(() => {
    setSelectAll(permissions.length > 0 && permissions.length === Items.length);
  }, [permissions]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return Items;
    return Items.filter(it => (it.title || '').toLowerCase().includes(q) || String(it.id).includes(q));
  }, [query]);

  const total = Items.length;
  const totalSelected = permissions.length;
  const indeterminate = totalSelected > 0 && totalSelected < total;

  const handleSelectAllChange = () => {
    if (selectAll) setPermissions([]);
    else setPermissions(Items.map((it) => it.id));
    setSelectAll(!selectAll);
    if (errors.permissions) setErrors(prev => ({ ...prev, permissions: '' }));
  };

  const handleCheckboxChange = (itemId) => {
    setPermissions(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
    if (errors.permissions) setErrors(prev => ({ ...prev, permissions: '' }));
  };

  const handleValidateAndOpenConfirm = () => {
    const newErrors = {};
    if (!formData.PERFIL || formData.PERFIL.trim() === '') newErrors.PERFIL = 'El nombre de perfil no puede estar vacío';
    if (permissions.length === 0) newErrors.permissions = 'Debe seleccionar al menos un permiso';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setConfirmSaveOpen(true);
  };

  const handleSavePermissions = async () => {
    setLoading(true);
    try {
      let valorAnterior = '';
      let valorNuevo = '';

      if (userId) {
        // Edición
        const resPrev = await axios.get(`${config.apiBaseUrl}/Permisos/${userId}`);
        const prevArr = resPrev.data?.permisos?.split(',').map(Number).filter(Boolean) || [];
        const nombresPrev = getNombresPermisos(prevArr);
        const added = permissions.filter(id => !prevArr.includes(id));
        const removed = prevArr.filter(id => !permissions.includes(id));
        const nombresAdded = getNombresPermisos(added);
        const nombresRemoved = getNombresPermisos(removed);

        valorAnterior = `Perfil: "${formData.PERFIL}", Permisos: ${nombresPrev || 'Sin permisos previos.'}`;
        valorNuevo = `Perfil: "${formData.PERFIL}"`;
        if (nombresAdded) valorNuevo += `, Permisos Agregados: ${nombresAdded}`;
        if (nombresRemoved) valorNuevo += `, Permisos Eliminados: ${nombresRemoved}`;
      } else {
        // Creación
        valorAnterior = 'Sin Registro';
        valorNuevo = `Nuevo perfil: ${formData.PERFIL}, Permisos: ${getNombresPermisos(permissions)}`;
      }

      // 🔹 Calculamos la ruta inicial automáticamente según los permisos
      const rutaInicial = getRutaInicialFromPermissions(permissions);


      const dataToSend = {
        PERFILID: userId || 0,
        PERFIL: formData.PERFIL,
        permisos: permissions.join(','),
        usuarioModificador: loggedInUsername,
        valor_anterior: valorAnterior,
        valor_nuevo: valorNuevo,
        rutaInicial, // 👈 Se manda al backend
      };



      await axios.post(`${config.apiBaseUrl}/crearEditarPerfil`, dataToSend);

      setSnackbarMessage(userId ? 'Perfil editado correctamente.' : 'Perfil creado correctamente.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      window.dispatchEvent(new Event('permisosCambiados'));
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error al guardar permisos:', error);
      const errorMessage =
        error.response?.data?.error ||
        'Hubo un error al guardar los permisos. Intente nuevamente.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        disableRestoreFocus
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,.18)' } }}
      >
        {/* Header azul */}
        <Box
          sx={{
            p: 1.5,
            background: `linear-gradient(90deg, ${ACCENT} 0%, #0B6BCB 100%)`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: '1px solid rgba(255,255,255,.15)'
          }}
        >
          <Avatar sx={{ width: 28, height: 28, bgcolor: 'white', color: ACCENT }}>
            <ShieldRoundedIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
              {userId ? 'Editar Perfil' : 'Crear Perfil'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.95 }}>
              Primero define el nombre del perfil y luego asigna los permisos
            </Typography>
          </Box>
          <Tooltip title="Cerrar">
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <DialogContent dividers sx={{ p: 2, bgcolor: '#fafafa' }}>
          {/* 1) Datos del perfil */}
          <Section
            title="Datos del perfil"
            subtitle="Este nombre identifica el perfil; no afecta directamente qué puede hacer."
            highlight
          >
            <TextField
              label="Nombre de Perfil"
              name="PERFIL"
              value={formData?.PERFIL || ''}
              onChange={(e) => {
                handleInputChange(e.target.name, e.target.value);
                if (errors.PERFIL) setErrors(prev => ({ ...prev, PERFIL: '' }));
              }}
              fullWidth
              size="small"
              margin="dense"
              error={Boolean(errors.PERFIL)}
              helperText={errors.PERFIL}
              sx={elegantInputSx}
            />
          </Section>

          {/* 2) Permisos */}
          <Section
            title="Permisos"
            subtitle="Activa solo los permisos que necesita este perfil."
          >
            {/* Toolbar: Búsqueda + Marcar todo */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mb: 1 }}
            >
              <TextField
                size="small"
                placeholder="Buscar permiso por nombre o ID…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ ...elegantInputSx, minWidth: 220, flex: 1 }}
              />

              <Tooltip title="Seleccionar todos los permisos">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectAll}
                      indeterminate={indeterminate}
                      onChange={handleSelectAllChange}
                      size="small"
                      sx={{ mr: 0.5 }}
                    />
                  }
                  label="Marcar todo"
                  sx={{ m: 0, whiteSpace: 'nowrap' }}
                />
              </Tooltip>
            </Stack>

            <Divider sx={{ mb: 1 }} />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {permissions.length} seleccionados de {Items.length}
              {query ? ` • Mostrando ${filteredItems.length}` : ''}
            </Typography>

            {/* Grid de permisos */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 1,
              }}
            >
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => {
                  const checked = permissions.includes(item.id);
                  return (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      sx={{
                        p: 0.5,
                        borderRadius: 1.5,
                        borderColor: checked ? 'primary.main' : 'divider',
                        bgcolor: checked ? 'primary.50' : 'background.paper',
                        transition: 'all .15s',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={() => handleCheckboxChange(item.id)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{item.title}</Typography>}
                      />
                    </Paper>
                  );
                })
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ gridColumn: '1 / -1', py: 2, textAlign: 'center' }}
                >
                  No hay coincidencias con “{query}”
                </Typography>
              )}
            </Box>

            {errors.permissions && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors.permissions}
              </Typography>
            )}
          </Section>
        </DialogContent>

        <DialogActions sx={{ py: 1, px: 2, bgcolor: '#f7f7f7' }}>
          <Button variant="outlined" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button variant="contained" onClick={handleValidateAndOpenConfirm} disabled={loading}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación final */}
      <DoubleConfirmModal
        open={confirmSaveOpen}
        message="¿Está seguro de que desea guardar los cambios?"
        handleConfirm={() => { setConfirmSaveOpen(false); handleSavePermissions(); }}
        handleCancel={() => setConfirmSaveOpen(false)}
      />

      {/* Spinners / Snackbar */}
      <SimpleSuccessSpinner
        open={loading || fetching}
        handleClose={() => {}}
        message={fetching ? 'Cargando permisos...' : 'Guardando permisos...'}
      />
      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
      />
    </>
  );
};

export default ModalPerfiles;
